import { promiseScreen } from "./promise.js";
import { permissionsScreen } from "./permissions.js";
import { homeScreen } from "./home.js";
import type { Session } from "../../platform/session.js";

export { promiseScreen, permissionsScreen, homeScreen };

export interface OnboardingCtx {
  session: Session;
  t: (k: string, p?: Record<string, string | number>) => string;
  defaultCell: string;
  /** Resolve the current location to a res-8 cell (or null if denied). Drives the
   *  first-run jump-to-my-location on the home step; falls back to defaultCell (Lisbon). */
  geolocate?: () => Promise<string | null>;
  done: () => void;
}

/** Three-screen flow: Promise → Permissions (JIT) → Home; `done()` once home/browse set. */
export function onboardingFlow(ctx: OnboardingCtx): HTMLElement {
  const outlet = document.createElement("div");
  outlet.className = "onboarding-flow";
  const show = (el: HTMLElement) => outlet.replaceChildren(el);

  const finishBrowse = async () => {
    await ctx.session.completeBrowse(ctx.defaultCell);
    ctx.done();
  };

  const goHome = () =>
    show(
      homeScreen({
        t: ctx.t,
        defaultCell: ctx.defaultCell,
        geolocate: ctx.geolocate,
        onConfirm: async (h) => {
          await ctx.session.setHome(h);
          ctx.done();
        },
        onBrowse: finishBrowse,
      }),
    );

  const goPerms = () => show(permissionsScreen({ t: ctx.t, onNext: goHome, onBrowse: finishBrowse }));

  const showPromise = () =>
    show(
      promiseScreen({
        t: ctx.t,
        onNext: goPerms,
        onRestore: () => {
          // Restore-my-identity UI is C6 (You tab / key backup). Stub for C2: proceed to setup.
          goPerms();
        },
        current: ctx.session.locale,
        onLocale: async (l) => {
          await ctx.session.setLocale(l);
          showPromise(); // re-render with the new catalog (ctx.t reads session.catalog live)
        },
      }),
    );

  showPromise();
  return outlet;
}
